import { invalidateCache, invalidateCachePartialMatch } from "@sg/query";
import { useMutation, useQuery } from "react-query";
import { z } from "zod";
import { agent } from "~/util/agent";

enum AssetClass {
  CRYPTO = "crypto",
  FIAT = "fiat",
  EQUITY = "equity",
}

const emptyToNull = (value: unknown) => (value === "" ? null : value);

const CurrencyReferenceFieldsSchema = z.object({
  isin: z.preprocess(emptyToNull, z.string().nullable().optional()),
  issuer_name: z.preprocess(emptyToNull, z.string().nullable().optional()),
  issuer_description: z.preprocess(emptyToNull, z.string().nullable().optional()),
  issuer_lei: z.preprocess(emptyToNull, z.string().nullable().optional()),
});

const CurrencySchema = CurrencyReferenceFieldsSchema.extend({
  name: z.string().trim().min(1),
  supported_base: z.boolean(),
  supported_counter: z.boolean(),
  enabled: z.boolean(),
  fiat: z.boolean(),
  asset_class: z.nativeEnum(AssetClass).optional(),
});

const UpdateCurrencySchema = CurrencyReferenceFieldsSchema;

type CurrencyResourceType = z.infer<typeof CurrencySchema> & {
  id?: string;
};
type UpdateCurrencyInput = z.infer<typeof UpdateCurrencySchema>;

const QUERY_CURRENCY_KEY = "currency";

const useCurrency = (id?: string) => {
  return useQuery(
    [QUERY_CURRENCY_KEY, id],
    async () => agent(`currencies/${id}`).json<CurrencyResourceType>(),
    {
      enabled: !!id,
    }
  );
};

const useCurrencies = () => {
  return useQuery([QUERY_CURRENCY_KEY], async () => agent("currencies").json<CurrencyResourceType[]>());
};

const useCreateCurrecy = () => {
  return useMutation<CurrencyResourceType, Error, CurrencyResourceType>(
    (data) => {
      return agent.post("currencies", { labels: ["created", "currency"], json: data }).json<CurrencyResourceType>();
    },
    {
      onSuccess() {
        invalidateCachePartialMatch([QUERY_CURRENCY_KEY]);
      },
    }
  );
};

const useUpdateCurrency = () => {
  return useMutation<unknown, Error, UpdateCurrencyInput & { id: string }>(
    (data) => {
      const { id, ...payload } = data;
      return agent.patch(`currencies/${id}`, { labels: ["updated", "currency"], json: payload });
    },
    {
      onSuccess(_data, variables) {
        invalidateCache([QUERY_CURRENCY_KEY, variables.id], variables);
        invalidateCachePartialMatch([QUERY_CURRENCY_KEY]);
      },
    }
  );
};

export {
  AssetClass,
  CurrencySchema,
  UpdateCurrencySchema,
  useCreateCurrecy,
  useCurrencies,
  useCurrency,
  useUpdateCurrency,
};
export type { CurrencyResourceType, UpdateCurrencyInput };
